import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/Dashboard';
import AdminTrips    from './pages/admin/operations/Trips';
import AdminBookings from './pages/admin/operations/Bookings';
import AdminParcels  from './pages/admin/operations/Parcels';
import AdminManifest from './pages/admin/operations/Manifest';
import AdminVehicles from './pages/admin/fleet/Vehicles';
import AdminDrivers from './pages/admin/fleet/Drivers';
import AdminRoutes from './pages/admin/fleet/Routes';
import AdminStops from './pages/admin/fleet/Stops';
import AdminFinance  from './pages/admin/finance/Overview';
import AdminExpenses from './pages/admin/finance/Expenses';
import AdminTransactions from './pages/admin/finance/Transactions';
import AdminWithdrawals from './pages/admin/finance/Withdrawals';
import ParcelRevenue from './pages/admin/finance/ParcelRevenue';
import AdminDaySummary from './pages/admin/finance/DaySummary';
import AdminUsers    from './pages/admin/people/Users';
import ManifestPlaceholder from './pages/admin/operations/ManifestPlaceholder';
import PublicLayout from './components/public/PublicLayout';
import PublicHome from './pages/public/Home';
import PublicTrips from './pages/public/Trips';
import TrackParcel from './pages/public/TrackParcel';
import NotFound from './pages/NotFound';

const MANAGEMENT_ROLES = ['admin', 'transport_manager', 'accountant'];
const ADMIN_CONSOLE_ROLES = ['admin', 'transport_manager', 'accountant', 'clerk'];

const RedirectManifest: React.FC = () => {
  const { tripId } = useParams();
  return <Navigate to={`/admin/operations/trips/${tripId}/manifest`} replace />;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user || !ADMIN_CONSOLE_ROLES.includes(user.role)) return <Navigate to="/login" />;
  return <>{children}</>;
};

const ManagementOnly = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  if (!user || !MANAGEMENT_ROLES.includes(user.role)) {
    return <Navigate to="/admin/operations/bookings" replace />;
  }
  return <>{children}</>;
};

const AdminOnly = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  if (!user || user.role !== 'admin') {
    return <Navigate to="/admin/operations/bookings" replace />;
  }
  return <>{children}</>;
};

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Auth */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* Admin (protected) */}
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<ManagementOnly><AdminDashboard /></ManagementOnly>} />

              {/* Operations — accessible by clerks */}
              <Route path="operations/trips" element={<AdminTrips />} />
              <Route path="operations/trips/:tripId/manifest" element={<AdminManifest />} />
              <Route path="operations/bookings" element={<AdminBookings />} />
              <Route path="operations/parcels" element={<AdminParcels />} />
              <Route path="operations/manifest" element={<ManifestPlaceholder />} />

              {/* Fleet — management only */}
              <Route path="fleet/vehicles" element={<ManagementOnly><AdminVehicles /></ManagementOnly>} />
              <Route path="fleet/drivers" element={<ManagementOnly><AdminDrivers /></ManagementOnly>} />
              <Route path="fleet/routes" element={<ManagementOnly><AdminRoutes /></ManagementOnly>} />
              <Route path="fleet/stops" element={<ManagementOnly><AdminStops /></ManagementOnly>} />

              {/* Finance — wallet management is admin-only; expenses/parcel-revenue stay management */}
              <Route path="finance" element={<AdminOnly><AdminFinance /></AdminOnly>} />
              <Route path="finance/expenses" element={<ManagementOnly><AdminExpenses /></ManagementOnly>} />
              <Route path="finance/transactions" element={<AdminOnly><AdminTransactions /></AdminOnly>} />
              <Route path="finance/withdrawals" element={<AdminOnly><AdminWithdrawals /></AdminOnly>} />
              <Route path="finance/parcel-revenue" element={<ManagementOnly><ParcelRevenue /></ManagementOnly>} />
              <Route path="finance/day-summary" element={<ManagementOnly><AdminDaySummary /></ManagementOnly>} />

              {/* People — admin only */}
              <Route path="people/users" element={<AdminOnly><AdminUsers /></AdminOnly>} />

              {/* Redirects from old flat paths */}
              <Route path="trips" element={<Navigate to="/admin/operations/trips" replace />} />
              <Route path="trips/:tripId/manifest" element={<RedirectManifest />} />
              <Route path="bookings" element={<Navigate to="/admin/operations/bookings" replace />} />
              <Route path="parcels" element={<Navigate to="/admin/operations/parcels" replace />} />
              <Route path="vehicles" element={<Navigate to="/admin/fleet/vehicles" replace />} />
              <Route path="users" element={<Navigate to="/admin/people/users" replace />} />
            </Route>

            {/* Public */}
            <Route path="/" element={<PublicLayout />}>
              <Route index element={<PublicHome />} />
              <Route path="trips" element={<PublicTrips />} />
              <Route path="track" element={<TrackParcel />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
